import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Activity,
  Bell,
  CheckCircle2,
  Globe2,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  RefreshCcw,
  Save,
  ShieldCheck,
  Sparkles,
  Watch,
  User,
  XCircle,
} from 'lucide-react';

import { athleteDevelopment, athletes, connectedTraining, wearables } from '../../lib/api';
import { resolveApiBaseUrl } from '../../lib/apiEnvironment';
import {
  buildHealthConnectScheduleRequest,
  buildHealthConnectSourceStatusReport,
  healthConnectAppInstanceId,
  healthConnectDeviceId,
  shouldAttemptHealthConnectAutoSchedule,
} from '../../lib/healthConnectAutoSchedule';
import {
  appleHealthAppInstanceId,
  appleHealthDeviceId,
  appleHealthLastProviderTimestamp,
  buildAppleHealthImportPayload,
  buildAppleHealthSourceStatusReport,
  shouldShowAppleHealthNativeAction,
} from '../../lib/appleHealthImport';
import { buildHealthConnectImportPayload, healthConnectLastProviderTimestamp } from '../../lib/healthConnectImport';
import {
  equipmentCapabilityTone,
  equipmentDeliveryStatusTone,
  formatEquipmentCapabilityStatus,
  summarizeEquipmentCapabilities,
  summarizeEquipmentDeliveries,
} from '../../lib/equipmentAutomationStatus';
import { useAuth } from '../../context/AuthContext';

const HealthConnect = registerPlugin('HealthConnect');
const AppleHealth = registerPlugin('AppleHealth');
const isAndroidNative = () => Capacitor.getPlatform() === 'android';
const isIosNative = () => Capacitor.getPlatform() === 'ios';

const emptyForm = () => ({
  age: '',
  body_weight_kg: '',
  date_of_birth: '',
  country: '',
  city: '',
  rank: '',
  dominant_hand: 'right',
  play_style: '',
  level: '',
});

const formFromProfile = (profile) => ({
  age: profile?.age || '',
  body_weight_kg: profile?.body_weight_kg || '',
  date_of_birth: profile?.date_of_birth || '',
  country: profile?.country || '',
  city: profile?.city || '',
  rank: profile?.rank || '',
  dominant_hand: profile?.dominant_hand || 'right',
  play_style: profile?.play_style || '',
  level: profile?.level || '',
});

const profileCompleteness = (profile) => {
  const fields = ['age', 'body_weight_kg', 'date_of_birth', 'country', 'city', 'rank', 'dominant_hand', 'play_style', 'level'];
  if (!profile) return 0;
  const filled = fields.filter((field) => profile[field]).length;
  return Math.round((filled / fields.length) * 100);
};

const initialsFor = (name, email) => {
  const source = (name || email || 'TD').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const roleLabel = (role, t) => {
  if (role === 'athlete') return t('auth.roleAthlete', 'Athlete');
  if (role === 'coach') return t('auth.roleCoach', 'Coach');
  if (role === 'admin') return t('auth.roleAdmin', 'Administrator');
  if (role === 'club') return t('auth.roleClub', 'Club');
  return role || 'User';
};

const wearableProviderLabel = (provider, t) => {
  const labels = {
    health_connect: t('profile.wearables.providers.healthConnect'),
    healthkit: t('profile.wearables.providers.appleHealth'),
    manual_import: t('profile.wearables.providers.manualImport'),
  };
  return labels[provider] || t('profile.wearables.providers.other');
};

const wearablePlatformLabel = (platform, t) => {
  const labels = {
    android: t('profile.wearables.platforms.android'),
    ios: t('profile.wearables.platforms.ios'),
    web: t('profile.wearables.platforms.web'),
    manual_import: t('profile.wearables.providers.manualImport'),
  };
  return labels[platform] || t('profile.wearables.providers.other');
};

const wearableStatusLabel = (status, t) => {
  const labels = {
    connected: t('profile.wearables.status.connected'),
    revoked: t('profile.wearables.status.revoked'),
    expired: t('profile.wearables.status.expired'),
    error: t('profile.wearables.status.error'),
    disabled: t('profile.wearables.status.disabled'),
  };
  return labels[status] || status || t('common.notSet');
};

const wearableStatusClass = (status) => {
  if (status === 'connected') return 'bg-lime-50 text-lime-700 ring-lime-100';
  if (status === 'error' || status === 'expired') return 'bg-rose-50 text-rose-700 ring-rose-100';
  if (status === 'revoked' || status === 'disabled') return 'bg-zinc-100 text-zinc-600 ring-zinc-200';
  return 'bg-blue-50 text-blue-700 ring-blue-100';
};

const backgroundSyncMessage = (status, t) => {
  if (status?.scheduled) {
    return t('profile.wearables.backgroundSyncScheduled');
  }
  const errorCode = status?.errorCode;
  if (errorCode === 'permission_required') return t('profile.wearables.backgroundSyncPermissionRequired');
  if (errorCode === 'auth_required') return t('profile.wearables.backgroundSyncAuthRequired');
  if (errorCode === 'sync_config_missing') return t('profile.wearables.backgroundSyncApiMissing');
  if (errorCode === 'health_connect_unavailable') return t('profile.wearables.healthConnectUnavailable');
  if (errorCode) return t('profile.wearables.backgroundSyncNotScheduled');
  return t('profile.wearables.backgroundSyncNotScheduled');
};

const backgroundSyncToneClass = (status) => {
  if (status?.scheduled) return 'bg-lime-50 text-lime-800 ring-lime-100';
  if (status?.errorCode) return 'bg-amber-50 text-amber-800 ring-amber-100';
  return 'bg-zinc-100 text-zinc-600 ring-zinc-200';
};

const hasAppleHealthRequiredPermissions = (result) =>
  Boolean(
    result?.hasRequiredPermissions ||
    result?.authorized ||
    result?.readAuthorized ||
    result?.status === 'authorized'
  );

const appleHealthGrantedPermissions = (result) => {
  if (Array.isArray(result?.grantedPermissions)) return result.grantedPermissions.filter(Boolean);
  if (Array.isArray(result?.permissions)) return result.permissions.filter(Boolean);
  if (Array.isArray(result?.authorizedTypes)) return result.authorizedTypes.filter(Boolean);
  return [];
};

const mobileSourceStatusLabel = (status, t) => {
  const labels = {
    scheduled: t('profile.wearables.syncStatusScheduled'),
    syncing: t('profile.wearables.syncStatusSyncing'),
    success: t('profile.wearables.syncStatusSuccess'),
    no_new_data: t('profile.wearables.syncStatusNoNewData'),
    permission_required: t('profile.wearables.syncStatusPermissionRequired'),
    auth_required: t('profile.wearables.syncStatusAuthRequired'),
    config_missing: t('profile.wearables.syncStatusConfigMissing'),
    failed: t('profile.wearables.syncStatusFailed'),
    stale: t('profile.wearables.syncStatusStale'),
    disabled: t('profile.wearables.syncStatusDisabled'),
  };
  return labels[status?.status] || t('profile.wearables.syncStatusDisabled');
};

const mobileSourceStatusToneClass = (status) => {
  const value = status?.status;
  if (value === 'success' || value === 'no_new_data' || value === 'scheduled' || value === 'syncing') {
    return 'bg-lime-50 text-lime-800 ring-lime-100';
  }
  if (value === 'permission_required' || value === 'auth_required' || value === 'config_missing' || value === 'stale') {
    return 'bg-amber-50 text-amber-800 ring-amber-100';
  }
  if (value === 'failed') return 'bg-rose-50 text-rose-800 ring-rose-100';
  return 'bg-zinc-100 text-zinc-600 ring-zinc-200';
};

const mobileSourceProviderLabel = (status, t) => {
  if (status?.source === 'health_connect') return t('profile.wearables.providers.healthConnect');
  if (status?.source === 'apple_health') return t('profile.wearables.providers.appleHealth');
  return status?.source || t('profile.wearables.providers.other');
};

const equipmentAutomationToneClass = (tone) => {
  if (tone === 'ready') return 'bg-lime-50 text-lime-800 ring-lime-100';
  if (tone === 'warning') return 'bg-amber-50 text-amber-800 ring-amber-100';
  if (tone === 'blocked') return 'bg-rose-50 text-rose-800 ring-rose-100';
  if (tone === 'neutral') return 'bg-blue-50 text-blue-800 ring-blue-100';
  return 'bg-zinc-100 text-zinc-600 ring-zinc-200';
};

const equipmentFallbackLabel = (mode, t) => {
  const labels = {
    phone_guided: t('profile.wearables.equipmentAutomation.fallbackPhoneGuided'),
    read_only: t('profile.wearables.equipmentAutomation.fallbackReadOnly'),
    manual: t('profile.wearables.equipmentAutomation.fallbackManual'),
  };
  return labels[mode] || mode || t('profile.wearables.equipmentAutomation.fallbackManual');
};

const formatWearableDate = (value, language) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(language || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const DEVELOPMENT_METRIC_GROUPS = [
  {
    titleKey: 'trainingLoad',
    source: 'training_load_profile',
    metrics: [
      ['training_count_7d'],
      ['training_duration_7d'],
      ['training_count_28d'],
      ['training_duration_28d'],
      ['match_count_28d'],
      ['load_label', ['latest_training_load_snapshot', 'load_label']],
      ['load_ratio', ['latest_training_load_snapshot', 'load_ratio']],
      ['active_calories_kcal', ['latest_training_load_snapshot', 'active_calories_kcal']],
    ],
  },
  {
    titleKey: 'wearables',
    source: 'wearable_profile',
    metrics: [
      ['connected_providers'],
      ['last_sync_at'],
      ['wearable_workouts_7d'],
      ['wearable_workouts_28d'],
      ['steps', ['wearable_samples_summary_28d', 'steps']],
      ['sleep_duration_min', ['wearable_samples_summary_28d', 'sleep_duration_min']],
      ['avg_heart_rate_bpm', ['wearable_samples_summary_28d', 'avg_heart_rate_bpm']],
    ],
  },
  {
    titleKey: 'matches',
    source: 'match_profile',
    metrics: [
      ['total_matches'],
      ['wins'],
      ['losses'],
      ['win_rate'],
      ['recent_match_count'],
    ],
  },
  {
    titleKey: 'recovery',
    source: 'recovery_profile',
    metrics: [
      ['recovery_log_count_28d'],
      ['latest_sleep_hours'],
      ['latest_resting_hr'],
      ['sleep_duration_min', ['latest_recovery_snapshot', 'sleep_duration_min']],
      ['readiness_score', ['latest_recovery_snapshot', 'readiness_score']],
    ],
  },
];

const getNestedValue = (source, path) =>
  path.reduce((current, key) => (current && Object.prototype.hasOwnProperty.call(current, key) ? current[key] : null), source);

const formatDevelopmentValue = (key, value, language) => {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    const safeValues = value.filter((item) => ['string', 'number', 'boolean'].includes(typeof item));
    return safeValues.length ? safeValues.join(', ') : null;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    if (key === 'win_rate' || key === 'load_ratio') return value.toFixed(2);
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }
  if (typeof value === 'string') {
    if (key.endsWith('_at') || key === 'date') return formatWearableDate(value, language);
    return value;
  }
  return null;
};

const developmentFieldLabel = (key, t) =>
  t(`profile.development.fields.${key}`, key.replace(/_/g, ' '));

const signalLabel = (item) => {
  if (!item) return null;
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return item.label || item.code || item.title || item.category || null;
};

const safeSignalList = (items) =>
  (Array.isArray(items) ? items : []).map(signalLabel).filter(Boolean);

const normalizeRecommendationList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const recommendationErrorKind = (error) => {
  const detail = error?.response?.data?.detail;
  const code = detail?.code || error?.response?.data?.code;
  if (error?.response?.status === 403 && code === 'upgrade_required') return 'upgrade';
  if (code === 'ai_integration_unavailable' || code === 'ai_generation_failed') return 'unavailable';
  return 'error';
};

const recommendationStatusLabel = (status, t) => {
  const normalized = String(status || 'active').trim().toLowerCase();
  return t(`profile.development.recommendations.status.${normalized}`, status || t('profile.development.recommendations.active'));
};

const recommendationEvidenceLabel = (item) => {
  if (!item) return null;
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  if (typeof item !== 'object') return null;
  return [item.section, item.field, item.ref, item.code, item.label]
    .filter(Boolean)
    .map((value) => String(value).replace(/_/g, ' '))
    .join(' / ');
};

const recommendationMetaLabel = (value, t) => {
  if (!value) return null;
  return t(`profile.development.recommendations.${String(value).toLowerCase()}`, value);
};

const buildMetricRows = (profile, group, t, language) => {
  const source = profile?.[group.source];
  if (!source || typeof source !== 'object' || Array.isArray(source)) return [];

  return group.metrics
    .map(([key, path]) => {
      const value = path ? getNestedValue(source, path) : source[key];
      const formatted = formatDevelopmentValue(key, value, language);
      if (!formatted) return null;
      return {
        key,
        label: developmentFieldLabel(key, t),
        value: formatted,
      };
    })
    .filter(Boolean);
};

const AvatarInitials = ({ user }) => (
  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-lime-300 text-2xl font-black tracking-normal text-zinc-950 shadow-sm ring-1 ring-lime-200">
    {initialsFor(user?.name, user?.email)}
  </div>
);

const EmailVerificationBadge = ({ user, t }) => {
  const verified = user?.is_verified === true;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black ring-1 ${
        verified
          ? 'bg-lime-50 text-lime-700 ring-lime-100'
          : 'bg-amber-50 text-amber-700 ring-amber-100'
      }`}
    >
      {verified ? <CheckCircle2 size={14} strokeWidth={1.8} /> : <XCircle size={14} strokeWidth={1.8} />}
      {verified
        ? t('profile.emailVerified', 'Email verified')
        : t('profile.emailNotVerified', 'Email not verified')}
    </span>
  );
};

const ProfileSkeleton = () => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="profile-loading">
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="h-56 animate-pulse rounded-lg bg-zinc-200" />
      <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="h-96 animate-pulse rounded-lg bg-zinc-200" />
        <div className="space-y-5">
          <div className="h-40 animate-pulse rounded-lg bg-zinc-200" />
          <div className="h-40 animate-pulse rounded-lg bg-zinc-200" />
        </div>
      </div>
    </div>
  </div>
);

const ProfileErrorState = ({ onRetry, t }) => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="profile-page">
    <div className="mx-auto max-w-7xl">
      <section className="rounded-lg border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
          <RefreshCcw size={24} strokeWidth={1.8} />
        </div>
        <h1 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
          {t('profile.loadError')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
          {t('profile.loadErrorBody', 'Refresh to load your profile and account settings.')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
        >
          <RefreshCcw size={16} strokeWidth={1.8} />
          {t('pwa.retry', 'Retry')}
        </button>
      </section>
    </div>
  </div>
);

const ProfileHeaderCard = ({ user, profile, t }) => {
  const completion = profileCompleteness(profile);
  return (
    <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7" data-testid="profile-identity">
      <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
      <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-lime-300 opacity-10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <AvatarInitials user={user} />
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
              {t('common.athleteDiary', 'Athlete diary')}
            </p>
            <h1 className="mt-2 truncate text-3xl font-black tracking-normal text-white md:text-4xl">
              {user?.name || t('profile.title')}
            </h1>
            <p className="mt-2 break-all text-sm text-zinc-300">{user?.email}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-black text-zinc-100 ring-1 ring-white/10">
                {roleLabel(user?.role, t)}
              </span>
              <EmailVerificationBadge user={user} t={t} />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-white/10 bg-white/10 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
            {t('profile.accountStatus', 'Account status')}
          </p>
          <div className="mt-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-5xl font-black tracking-normal text-white">{completion}%</p>
              <p className="mt-1 text-sm text-zinc-300">
                {t('profile.profileComplete', 'Profile complete')}
              </p>
            </div>
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-lime-300 text-zinc-950">
              <ShieldCheck size={30} strokeWidth={1.8} />
            </div>
          </div>
          <div className="mt-5 h-3 rounded-full bg-zinc-950/50">
            <div className="h-3 rounded-full bg-lime-300" style={{ width: `${completion}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
};

const SectionHeader = ({ icon, kicker, title, body }) => (
  <div className="mb-5 flex items-start gap-3">
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      {icon}
    </div>
    <div>
      {kicker ? (
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{kicker}</p>
      ) : null}
      <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">{title}</h2>
      {body ? <p className="mt-1 text-sm leading-6 text-zinc-500">{body}</p> : null}
    </div>
  </div>
);

const TextInput = ({ label, value, onChange, testId, type = 'text', placeholder }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-700">{label}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
    />
  </label>
);

const SelectInput = ({ label, value, onChange, testId, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-700">{label}</span>
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="min-h-11 w-full rounded-lg border border-zinc-200 bg-white px-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:ring-2 focus:ring-lime-100"
    >
      {children}
    </select>
  </label>
);

const ProfileForm = ({ formData, setFormData, onSubmit, t, saving }) => {
  const update = (field) => (event) =>
    setFormData({ ...formData, [field]: event.target.value });

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={<User size={21} strokeWidth={1.8} />}
        kicker={t('profile.details', 'Details')}
        title={t('profile.subtitle')}
        body={t('profile.formHint', 'Keep your tennis profile current for better matching and progress context.')}
      />
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <TextInput
            label={t('profile.age')}
            type="number"
            value={formData.age}
            onChange={update('age')}
            testId="input-age"
          />
          <TextInput
            label={t('profile.bodyWeightKg')}
            type="number"
            value={formData.body_weight_kg}
            onChange={update('body_weight_kg')}
            testId="input-body-weight-kg"
          />
          <TextInput
            label={t('profile.dateOfBirth')}
            type="date"
            value={formData.date_of_birth}
            onChange={update('date_of_birth')}
            testId="input-date-of-birth"
          />
          <TextInput
            label={t('profile.rank')}
            value={formData.rank}
            onChange={update('rank')}
            testId="input-rank"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <TextInput
            label={t('profile.country')}
            value={formData.country}
            onChange={update('country')}
            testId="input-country"
          />
          <TextInput
            label={t('profile.city')}
            value={formData.city}
            onChange={update('city')}
            testId="input-city"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <SelectInput
            label={t('profile.dominantHand')}
            value={formData.dominant_hand}
            onChange={update('dominant_hand')}
            testId="input-dominant-hand"
          >
            <option value="right">{t('profile.handRight')}</option>
            <option value="left">{t('profile.handLeft')}</option>
          </SelectInput>
          <TextInput
            label={t('profile.level')}
            value={formData.level}
            onChange={update('level')}
            placeholder={t('profile.levelPlaceholder')}
            testId="input-level"
          />
        </div>

        <TextInput
          label={t('profile.playStyle')}
          value={formData.play_style}
          onChange={update('play_style')}
          placeholder={t('profile.playStylePlaceholder')}
          testId="input-play-style"
        />

        <button
          type="submit"
          disabled={saving}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="save-profile"
        >
          <Save size={16} strokeWidth={1.8} />
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </form>
    </section>
  );
};

const DevelopmentPillList = ({ title, items, t, tone = 'zinc' }) => {
  const toneClass =
    tone === 'amber'
      ? 'bg-amber-50 text-amber-800 ring-amber-100'
      : 'bg-zinc-100 text-zinc-700 ring-zinc-200';

  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">{title}</p>
      {items.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {items.map((item) => (
            <span key={item} className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${toneClass}`}>
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold text-zinc-500">
          {t('profile.development.moreData')}
        </p>
      )}
    </div>
  );
};

const DevelopmentMetricGroup = ({ title, rows }) => {
  if (!rows.length) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">{title}</p>
      <div className="mt-3 grid gap-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
            <span className="font-bold text-zinc-500">{row.label}</span>
            <span className="truncate text-right font-black text-zinc-950">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DevelopmentRecommendationCard = ({ recommendation, t, language }) => {
  const evidence = normalizeRecommendationList(recommendation?.evidence_refs)
    .map(recommendationEvidenceLabel)
    .filter(Boolean)
    .slice(0, 4);
  const createdAt = formatWearableDate(recommendation?.created_at, language);
  const expiresAt = recommendation?.expires_at ? formatWearableDate(recommendation.expires_at, language) : null;

  return (
    <article className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {recommendationMetaLabel(recommendation?.category, t)}
          </p>
          <h3 className="mt-1 font-black text-zinc-950">{recommendation?.title}</h3>
          {createdAt ? <p className="mt-1 text-xs font-bold text-zinc-400">{createdAt}</p> : null}
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-zinc-700 ring-1 ring-zinc-200">
          {recommendationStatusLabel(recommendation?.status, t)}
        </span>
      </div>

      <p className="mt-3 text-sm leading-6 text-zinc-600">{recommendation?.rationale}</p>

      <div className="mt-4 grid gap-2 text-xs font-bold text-zinc-600 sm:grid-cols-2">
        <span className="rounded-lg bg-white px-3 py-2 ring-1 ring-zinc-200">
          {t('profile.development.recommendations.priority')}: {recommendationMetaLabel(recommendation?.priority, t) || '-'}
        </span>
        <span className="rounded-lg bg-white px-3 py-2 ring-1 ring-zinc-200">
          {t('profile.development.recommendations.confidence')}: {recommendationMetaLabel(recommendation?.confidence, t) || '-'}
        </span>
        {expiresAt ? (
          <span className="rounded-lg bg-white px-3 py-2 ring-1 ring-zinc-200">
            {t('profile.development.recommendations.expires')}: {expiresAt}
          </span>
        ) : null}
      </div>

      {evidence.length ? (
        <div className="mt-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
            {t('profile.development.recommendations.evidence')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {evidence.map((item) => (
              <span key={item} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-zinc-600 ring-1 ring-zinc-200">
                {item}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
};

const DevelopmentRecommendationsSection = ({
  recommendations,
  loading,
  loadFailed,
  messageKind,
  generating,
  onGenerate,
  t,
  language,
}) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
          {t('profile.development.recommendations.paid')}
        </p>
        <h3 className="mt-1 text-lg font-black text-zinc-950">
          {t('profile.development.recommendations.title')}
        </h3>
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating}
        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-3 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} strokeWidth={1.8} />}
        {generating
          ? t('profile.development.recommendations.generating')
          : t('profile.development.recommendations.generate')}
      </button>
    </div>

    <p className="mt-3 rounded-lg bg-lime-50 px-3 py-2 text-xs font-bold leading-5 text-lime-800 ring-1 ring-lime-100">
      {t('profile.development.recommendations.safetyNote')}
    </p>

    {loading ? (
      <div className="mt-4 grid gap-3">
        <div className="h-24 animate-pulse rounded-lg bg-zinc-100" />
        <div className="h-24 animate-pulse rounded-lg bg-zinc-100" />
      </div>
    ) : loadFailed ? (
      <div className="mt-4 rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
        {messageKind === 'upgrade'
          ? `${t('profile.development.recommendations.upgradeRequired')}. ${t('profile.development.recommendations.paidOnly')}`
          : t('profile.development.recommendations.unavailable')}
      </div>
    ) : recommendations.length ? (
      <div className="mt-4 grid gap-3">
        {recommendations.map((recommendation) => (
          <DevelopmentRecommendationCard
            key={recommendation.id || `${recommendation.title}-${recommendation.created_at}`}
            recommendation={recommendation}
            t={t}
            language={language}
          />
        ))}
      </div>
    ) : (
      <div className="mt-4 rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4">
        <p className="font-black text-zinc-950">
          {t('profile.development.recommendations.emptyTitle')}
        </p>
        <p className="mt-2 text-sm leading-6 text-zinc-500">
          {t('profile.development.recommendations.emptyBody')}
        </p>
      </div>
    )}
  </div>
);

const AthleteDevelopmentProfileCard = ({ t, i18n }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);
  const [recommendationsFailed, setRecommendationsFailed] = useState(false);
  const [recommendationsMessageKind, setRecommendationsMessageKind] = useState(null);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);

  const fetchDevelopmentProfile = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await athleteDevelopment.getProfile();
      setProfile(response.data || null);
    } catch (error) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevelopmentProfile();
  }, [fetchDevelopmentProfile]);

  const fetchRecommendations = useCallback(async () => {
    setRecommendationsLoading(true);
    setRecommendationsFailed(false);
    setRecommendationsMessageKind(null);
    try {
      const response = await athleteDevelopment.getRecommendations();
      setRecommendations(normalizeRecommendationList(response.data));
    } catch (error) {
      setRecommendations([]);
      setRecommendationsFailed(true);
      setRecommendationsMessageKind(recommendationErrorKind(error));
    } finally {
      setRecommendationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const handleGenerateRecommendations = async () => {
    setGeneratingRecommendations(true);
    setRecommendationsFailed(false);
    setRecommendationsMessageKind(null);
    try {
      const response = await athleteDevelopment.generateRecommendations();
      setRecommendations(normalizeRecommendationList(response.data));
      await fetchRecommendations();
      toast.success(t('profile.development.recommendations.generatedSuccess'));
    } catch (error) {
      setRecommendationsMessageKind(recommendationErrorKind(error));
      setRecommendationsFailed(true);
      toast.error(
        recommendationErrorKind(error) === 'upgrade'
          ? t('profile.development.recommendations.paidOnly')
          : t('profile.development.recommendations.unavailable')
      );
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const metricGroups = DEVELOPMENT_METRIC_GROUPS.map((group) => ({
    title: t(`profile.development.${group.titleKey}`),
    rows: buildMetricRows(profile, group, t, i18n.language),
  }));
  const hasMetricRows = metricGroups.some((group) => group.rows.length);
  const strengths = safeSignalList(profile?.strengths);
  const weaknesses = safeSignalList(profile?.weaknesses);
  const focusAreas = safeSignalList(profile?.next_focus_areas);
  const riskFlags = safeSignalList(profile?.risk_flags);
  const dataQualityFlags = safeSignalList(profile?.data_quality_flags);
  const confidenceRows = Object.entries(profile?.ai_confidence || {})
    .map(([key, value]) => ({
      key,
      label: developmentFieldLabel(key, t),
      value: formatDevelopmentValue(key, value, i18n.language),
    }))
    .filter((row) => row.value);
  const hasSignals =
    strengths.length ||
    weaknesses.length ||
    focusAreas.length ||
    riskFlags.length ||
    dataQualityFlags.length ||
    confidenceRows.length;
  const profileDates = [
    profile?.updated_at
      ? [t('profile.development.lastUpdated'), formatWearableDate(profile.updated_at, i18n.language)]
      : null,
    profile?.generated_at
      ? [t('profile.development.generated'), formatWearableDate(profile.generated_at, i18n.language)]
      : null,
  ].filter(Boolean);

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={<Activity size={21} strokeWidth={1.8} />}
        kicker={t('profile.development.summary')}
        title={t('profile.development.title')}
        body={t('profile.development.safetyNote')}
      />

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="h-24 animate-pulse rounded-lg bg-zinc-100" />
          <div className="h-24 animate-pulse rounded-lg bg-zinc-100" />
        </div>
      ) : loadFailed ? (
        <div className="rounded-lg bg-rose-50 p-4 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          <p>{t('profile.development.loadError')}</p>
          <button
            type="button"
            onClick={fetchDevelopmentProfile}
            className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-sm font-black text-rose-700 transition hover:bg-rose-100"
          >
            <RefreshCcw size={15} strokeWidth={1.8} />
            {t('pwa.retry', 'Retry')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <div className="rounded-lg bg-lime-50 px-3 py-2 font-bold text-lime-800 ring-1 ring-lime-100">
              {t('profile.development.guidanceOnly')}
            </div>
            {profileDates.length ? (
              profileDates.map(([label, value]) => (
                <div key={label} className="rounded-lg bg-zinc-50 px-3 py-2 font-bold text-zinc-600">
                  {label}: {value}
                </div>
              ))
            ) : (
              <div className="rounded-lg bg-zinc-50 px-3 py-2 font-bold text-zinc-600">
                {t('profile.development.moreData')}
              </div>
            )}
          </div>

          {confidenceRows.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('profile.development.confidence')}
              </p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {confidenceRows.map((row) => (
                  <div key={row.key} className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
                    <span className="font-bold text-zinc-500">{row.label}</span>
                    <span className="font-black text-zinc-950">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {hasMetricRows ? (
            <div className="grid gap-3 xl:grid-cols-2">
              {metricGroups.map((group) => (
                <DevelopmentMetricGroup key={group.title} title={group.title} rows={group.rows} />
              ))}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <DevelopmentPillList title={t('profile.development.dataQuality')} items={dataQualityFlags} t={t} />
            <DevelopmentPillList title={t('profile.development.operationalFlags')} items={riskFlags} t={t} tone="amber" />
            <DevelopmentPillList title={t('profile.development.strengths')} items={strengths} t={t} />
            <DevelopmentPillList title={t('profile.development.focusAreas')} items={focusAreas} t={t} />
            <DevelopmentPillList title={t('profile.development.areasToImprove')} items={weaknesses} t={t} />
          </div>

          {!hasMetricRows && !hasSignals ? (
            <div className="rounded-lg bg-zinc-50 p-4 text-sm font-bold text-zinc-500">
              {t('profile.development.moreData')}
            </div>
          ) : null}

          <DevelopmentRecommendationsSection
            recommendations={recommendations}
            loading={recommendationsLoading}
            loadFailed={recommendationsFailed}
            messageKind={recommendationsMessageKind}
            generating={generatingRecommendations}
            onGenerate={handleGenerateRecommendations}
            t={t}
            language={i18n.language}
          />
        </div>
      )}
    </section>
  );
};

const SettingsRow = ({ icon, title, body, action, muted = false }) => (
  <div className={`flex items-center justify-between gap-4 rounded-lg border border-zinc-200 p-4 ${muted ? 'bg-zinc-50' : 'bg-white'}`}>
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="font-black text-zinc-950">{title}</p>
        {body ? <p className="mt-1 text-sm leading-5 text-zinc-500">{body}</p> : null}
      </div>
    </div>
    {action}
  </div>
);

const TogglePill = ({ enabled }) => (
  <span
    className={`inline-flex h-6 w-11 items-center rounded-full p-1 transition ${
      enabled ? 'bg-lime-400' : 'bg-zinc-200'
    }`}
  >
    <span className={`h-4 w-4 rounded-full bg-white shadow-sm transition ${enabled ? 'translate-x-5' : ''}`} />
  </span>
);

const LanguageSelector = ({ i18n, t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <SectionHeader
      icon={<Globe2 size={21} strokeWidth={1.8} />}
      kicker={t('nav.settings')}
      title={t('nav.language')}
      body={t('profile.languageHint', 'Choose the interface language for this browser.')}
    />
    <div className="grid grid-cols-3 gap-2">
      {[
        ['ru', 'RU'],
        ['en', 'EN'],
        ['fr', 'FR'],
      ].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => i18n.changeLanguage(code)}
          className={`min-h-11 rounded-lg border px-3 text-sm font-black transition ${
            i18n.language === code
              ? 'border-lime-300 bg-lime-300 text-zinc-950'
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </section>
);

const SecurityCard = ({ user, t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <SectionHeader
      icon={<LockKeyhole size={21} strokeWidth={1.8} />}
      kicker={t('profile.security', 'Security')}
      title={t('profile.accountSecurity', 'Account security')}
      body={t('profile.securityHint', 'Use existing account flows for password and email verification.')}
    />
    <div className="space-y-3">
      <SettingsRow
        icon={<Mail size={18} strokeWidth={1.8} />}
        title={t('profile.emailStatus', 'Email status')}
        body={user?.email}
        action={<EmailVerificationBadge user={user} t={t} />}
      />
      {!user?.is_verified ? (
        <Link
          to="/check-email"
          state={{ email: user?.email }}
          className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-4 text-sm font-black text-amber-800 transition hover:bg-amber-100"
        >
          {t('auth.emailVerificationKicker', 'Email verification')}
        </Link>
      ) : null}
      <SettingsRow
        icon={<ShieldCheck size={18} strokeWidth={1.8} />}
        title={t('profile.twoFactor', 'Two-factor authentication')}
        body={t('profile.twoFactorFallback', 'Advanced sign-in controls are not enabled in this profile view.')}
        action={<TogglePill enabled={false} />}
        muted
      />
    </div>
  </section>
);

const PasswordActionCard = ({ t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <SectionHeader
      icon={<LockKeyhole size={21} strokeWidth={1.8} />}
      kicker={t('auth.password')}
      title={t('profile.passwordAccess', 'Password access')}
      body={t('profile.passwordHint', 'Password changes use the existing reset-code flow.')}
    />
    <Link
      to="/forgot-password"
      className="inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
    >
      {t('profile.resetPassword', 'Reset password')}
    </Link>
  </section>
);

const NotificationPreferenceCard = ({ t }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
    <SectionHeader
      icon={<Bell size={21} strokeWidth={1.8} />}
      kicker={t('nav.notifications')}
      title={t('profile.notificationPreferences', 'Notification preferences')}
      body={t('profile.notificationHint', 'Email and reminder preferences are managed in the existing notifications page.')}
    />
    <SettingsRow
      icon={<Bell size={18} strokeWidth={1.8} />}
      title={t('notification.emailNotifications', 'Email notifications')}
      body={t('profile.notificationFallback', 'Open notification settings to edit supported preferences.')}
      action={
        <Link
          to="/notifications"
          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50"
        >
          {t('nav.settings')}
        </Link>
      }
    />
  </section>
);

const WearableConnectionRow = ({
  connection,
  onDisconnect,
  onSync,
  disconnecting,
  syncing,
  canSyncNative,
  syncingLabel,
  t,
  i18n,
}) => {
  const scopes = Array.isArray(connection?.granted_scopes)
    ? connection.granted_scopes.filter(Boolean)
    : [];
  const canDisconnect = connection?.status === 'connected';

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-black text-zinc-950">
            {connection?.device_label || wearableProviderLabel(connection?.provider, t)}
          </p>
          <p className="mt-1 text-sm text-zinc-500">
            {wearableProviderLabel(connection?.provider, t)}
          </p>
        </div>
        <span className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ring-1 ${wearableStatusClass(connection?.status)}`}>
          {wearableStatusLabel(connection?.status, t)}
        </span>
      </div>

      <div className="mt-4 grid gap-2 text-sm">
        {[
          [t('profile.wearables.provider'), wearableProviderLabel(connection?.provider, t)],
          [t('profile.wearables.platform'), wearablePlatformLabel(connection?.platform, t)],
          [t('profile.wearables.statusLabel'), wearableStatusLabel(connection?.status, t)],
          [t('profile.wearables.lastSync'), formatWearableDate(connection?.last_sync_at, i18n.language)],
        ].map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
            <span className="font-bold text-zinc-500">{label}</span>
            <span className="truncate text-right font-black text-zinc-950">{value}</span>
          </div>
        ))}
      </div>

      {scopes.length ? (
        <div className="mt-3">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
            {t('profile.wearables.grantedData')}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {scopes.map((scope) => (
              <span key={scope} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-bold text-zinc-700">
                {scope}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {connection?.error_code ? (
        <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('profile.wearables.connectionError')}: {connection.error_code}
        </div>
      ) : null}

      {canDisconnect ? (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {canSyncNative ? (
            <button
              type="button"
              onClick={() => onSync(connection)}
              disabled={syncing}
              className="inline-flex min-h-10 items-center justify-center rounded-lg border border-lime-300 bg-lime-300 px-3 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {syncing ? syncingLabel || t('profile.wearables.syncingWearableData') : t('profile.wearables.syncNow')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onDisconnect(connection.id)}
            disabled={disconnecting}
            className="inline-flex min-h-10 items-center justify-center rounded-lg border border-zinc-200 bg-white px-3 text-sm font-black text-zinc-700 transition hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={t('profile.wearables.disconnectDevice')}
          >
            {disconnecting ? t('common.loading') : t('profile.wearables.disconnect')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const WearablesCard = ({ t, i18n, user }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [connectingHealthConnect, setConnectingHealthConnect] = useState(false);
  const [connectingAppleHealth, setConnectingAppleHealth] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [backgroundSyncStatus, setBackgroundSyncStatus] = useState(null);
  const [mobileSourceStatuses, setMobileSourceStatuses] = useState([]);
  const [equipmentCapabilities, setEquipmentCapabilities] = useState([]);
  const [equipmentDeliveries, setEquipmentDeliveries] = useState([]);
  const [equipmentAutomationLoading, setEquipmentAutomationLoading] = useState(false);
  const autoScheduleAttemptedRef = useRef(false);
  const androidNative = isAndroidNative();
  const iosNative = isIosNative();
  const appleHealthNativeAction = shouldShowAppleHealthNativeAction({
    nativeApp: typeof Capacitor.isNativePlatform === 'function'
      ? Capacitor.isNativePlatform()
      : Capacitor.getPlatform() !== 'web',
    platform: Capacitor.getPlatform(),
  });

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await wearables.getConnections();
      setConnections(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const refreshMobileSourceStatuses = useCallback(async () => {
    try {
      const response = await connectedTraining.getMobileSourceStatuses();
      setMobileSourceStatuses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMobileSourceStatuses([]);
    }
  }, []);

  useEffect(() => {
    refreshMobileSourceStatuses();
  }, [refreshMobileSourceStatuses]);

  const refreshEquipmentAutomationStatus = useCallback(async () => {
    setEquipmentAutomationLoading(true);
    try {
      const [capabilitiesResponse, deliveriesResponse] = await Promise.all([
        connectedTraining.getRecentEquipmentCapabilities({ limit: 20 }),
        connectedTraining.getRecentEquipmentWorkoutDeliveries({ limit: 20 }),
      ]);
      setEquipmentCapabilities(Array.isArray(capabilitiesResponse.data) ? capabilitiesResponse.data : []);
      setEquipmentDeliveries(Array.isArray(deliveriesResponse.data) ? deliveriesResponse.data : []);
    } catch (error) {
      setEquipmentCapabilities([]);
      setEquipmentDeliveries([]);
    } finally {
      setEquipmentAutomationLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshEquipmentAutomationStatus();
  }, [refreshEquipmentAutomationStatus]);

  const refreshBackgroundSyncStatus = useCallback(async () => {
    if (!androidNative) return;
    try {
      const status = await HealthConnect.getHealthConnectBackgroundSyncStatus();
      setBackgroundSyncStatus(status || null);
    } catch (error) {
      setBackgroundSyncStatus(null);
    }
  }, [androidNative]);

  useEffect(() => {
    refreshBackgroundSyncStatus();
  }, [refreshBackgroundSyncStatus]);

  const handleDisconnect = async (connectionId) => {
    setDisconnectingId(connectionId);
    try {
      await wearables.disconnectConnection(connectionId);
      await fetchConnections();
      toast.success(t('profile.wearables.disconnectSuccess'));
    } catch (error) {
      toast.error(t('profile.wearables.disconnectError'));
    } finally {
      setDisconnectingId(null);
    }
  };

  const upsertHealthConnectConnection = useCallback(async (grantedPermissions) => {
    const payload = {
      provider: 'health_connect',
      platform: 'android',
      status: 'connected',
      granted_scopes: grantedPermissions,
      device_label: 'Health Connect',
    };
    const existingConnection = connections.find(
      (connection) => connection?.provider === 'health_connect' && connection?.platform === 'android'
    );

    if (existingConnection?.id) {
      const response = await wearables.updateConnection(existingConnection.id, payload);
      return response?.data || { ...existingConnection, ...payload };
    } else {
      const response = await wearables.createConnection(payload);
      return response?.data || payload;
    }
  }, [connections]);

  const upsertAppleHealthConnection = useCallback(async (grantedPermissions) => {
    const payload = {
      provider: 'healthkit',
      platform: 'ios',
      status: 'connected',
      granted_scopes: grantedPermissions,
      device_label: 'Apple Health',
    };
    const existingConnection = connections.find(
      (connection) => connection?.provider === 'healthkit' && connection?.platform === 'ios'
    );

    if (existingConnection?.id) {
      const response = await wearables.updateConnection(existingConnection.id, payload);
      return response?.data || { ...existingConnection, ...payload };
    } else {
      const response = await wearables.createConnection(payload);
      return response?.data || payload;
    }
  }, [connections]);

  const scheduleHealthConnectBackgroundSync = useCallback(async (connection, lastProviderTimestamp = null) => {
    if (!androidNative) return null;
    const apiUrl = resolveApiBaseUrl({ nativeApp: true });
    try {
      const result = await HealthConnect.scheduleHealthConnectBackgroundSync(buildHealthConnectScheduleRequest({
        apiUrl,
        connection,
        lastProviderTimestamp,
      }));
      setBackgroundSyncStatus(result || null);
      return result;
    } catch (error) {
      const failedStatus = { scheduled: false, errorCode: 'schedule_failed' };
      setBackgroundSyncStatus(failedStatus);
      return failedStatus;
    }
  }, [androidNative]);

  const notifyBackgroundSyncIssue = (status) => {
    if (!status || status.scheduled || !status.errorCode) return;
    toast.error(backgroundSyncMessage(status, t));
  };

  const reportHealthConnectSourceStatus = useCallback(async (payload = {}, connection = null) => {
    try {
      const deviceId = payload.device_id || healthConnectDeviceId(connection);
      const appInstanceId = payload.app_instance_id || healthConnectAppInstanceId(connection);
      await connectedTraining.reportMobileSourceStatus({
        source: 'health_connect',
        platform: 'android',
        device_id: deviceId,
        app_instance_id: appInstanceId,
        background_sync_enabled: Boolean(payload.background_sync_enabled),
        ...payload,
      });
      await refreshMobileSourceStatuses();
    } catch (error) {
      // Backend-visible source status is best-effort from the UI.
    }
  }, [refreshMobileSourceStatuses]);

  const reportAppleHealthSourceStatus = useCallback(async (payload = {}, connection = null) => {
    try {
      const deviceId = payload.device_id || appleHealthDeviceId(connection);
      const appInstanceId = payload.app_instance_id || appleHealthAppInstanceId(connection);
      await connectedTraining.reportMobileSourceStatus({
        source: 'apple_health',
        platform: 'ios',
        device_id: deviceId,
        app_instance_id: appInstanceId,
        ...buildAppleHealthSourceStatusReport(payload),
      });
      await refreshMobileSourceStatuses();
    } catch (error) {
      // Backend-visible source status is best-effort from the UI.
    }
  }, [refreshMobileSourceStatuses]);

  useEffect(() => {
    autoScheduleAttemptedRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (!shouldAttemptHealthConnectAutoSchedule({
      androidNative,
      authenticated: Boolean(user?.id),
      loading,
      alreadyAttempted: autoScheduleAttemptedRef.current,
    })) {
      return undefined;
    }

    autoScheduleAttemptedRef.current = true;
    let canceled = false;

    const autoSchedule = async () => {
      try {
        const availability = await HealthConnect.isHealthConnectAvailable();
        if (canceled) return;
        if (!availability?.available) {
          const status = { scheduled: false, errorCode: 'health_connect_unavailable' };
          setBackgroundSyncStatus(status);
          await reportHealthConnectSourceStatus({
            status: 'failed',
            background_sync_enabled: false,
            error_code: 'health_connect_unavailable',
          });
          return;
        }

        const granted = await HealthConnect.getGrantedPermissions();
        if (canceled) return;
        if (!granted?.hasRequiredPermissions) {
          const status = { scheduled: false, status: 'permission_required', errorCode: 'permission_required' };
          setBackgroundSyncStatus(status);
          await reportHealthConnectSourceStatus({
            status: 'permission_required',
            background_sync_enabled: false,
            error_code: 'permission_required',
          });
          return;
        }

        const grantedPermissions = Array.isArray(granted?.grantedPermissions)
          ? granted.grantedPermissions.filter(Boolean)
          : [];
        const connection = await upsertHealthConnectConnection(grantedPermissions);
        if (canceled) return;
        const backgroundStatus = await scheduleHealthConnectBackgroundSync(connection);
        if (canceled) return;
        await reportHealthConnectSourceStatus(buildHealthConnectSourceStatusReport(backgroundStatus), connection);
        if (backgroundStatus?.scheduled) {
          await fetchConnections();
        }
      } catch (error) {
        if (canceled) return;
        const status = { scheduled: false, errorCode: 'auto_schedule_failed' };
        setBackgroundSyncStatus(status);
        await reportHealthConnectSourceStatus({
          status: 'failed',
          background_sync_enabled: false,
          error_code: 'auto_schedule_failed',
        });
      }
    };

    autoSchedule();

    return () => {
      canceled = true;
    };
  }, [
    androidNative,
    fetchConnections,
    loading,
    reportHealthConnectSourceStatus,
    scheduleHealthConnectBackgroundSync,
    upsertHealthConnectConnection,
    user?.id,
  ]);

  const handleConnectHealthConnect = async () => {
    setConnectingHealthConnect(true);
    try {
      const availability = await HealthConnect.isHealthConnectAvailable();
      if (!availability?.available) {
        toast.error(t('profile.wearables.healthConnectUnavailable'));
        return;
      }

      const result = await HealthConnect.requestHealthConnectPermissions();
      const grantedPermissions = Array.isArray(result?.grantedPermissions)
        ? result.grantedPermissions.filter(Boolean)
        : [];

      if (!result?.hasRequiredPermissions || grantedPermissions.length === 0) {
        await reportHealthConnectSourceStatus({
          status: 'permission_required',
          background_sync_enabled: false,
          error_code: 'permission_required',
        });
        toast.error(t('profile.wearables.healthPermissionsRequired'));
        return;
      }

      const connection = await upsertHealthConnectConnection(grantedPermissions);
      const backgroundStatus = await scheduleHealthConnectBackgroundSync(connection);
      await reportHealthConnectSourceStatus(buildHealthConnectSourceStatusReport(backgroundStatus), connection);
      await fetchConnections();
      if (backgroundStatus?.scheduled) {
        toast.success(t('profile.wearables.backgroundSyncScheduled'));
      } else {
        notifyBackgroundSyncIssue(backgroundStatus);
      }
    } catch (error) {
      toast.error(t('profile.wearables.permissionRequestCancelled'));
    } finally {
      setConnectingHealthConnect(false);
    }
  };

  const handleOpenHealthConnect = async () => {
    try {
      await HealthConnect.openHealthConnect();
    } catch (error) {
      toast.error(t('profile.wearables.healthConnectUnavailable'));
    }
  };

  const ensureHealthConnectPermissions = async () => {
    const availability = await HealthConnect.isHealthConnectAvailable();
    if (!availability?.available) {
      toast.error(t('profile.wearables.healthConnectUnavailable'));
      return false;
    }

    const granted = await HealthConnect.getGrantedPermissions();
    if (granted?.hasRequiredPermissions) {
      return true;
    }

    const requested = await HealthConnect.requestHealthConnectPermissions();
    if (!requested?.hasRequiredPermissions) {
      toast.error(t('profile.wearables.healthPermissionsRequired'));
      return false;
    }
    return true;
  };

  const handleSyncHealthConnect = async (connection) => {
    if (!connection?.id) return;
    setSyncingId(connection.id);
    try {
      const hasPermissions = await ensureHealthConnectPermissions();
      if (!hasPermissions) {
        await reportHealthConnectSourceStatus({
          status: 'permission_required',
          background_sync_enabled: Boolean(backgroundSyncStatus?.scheduled),
          error_code: 'permission_required',
        }, connection);
        return;
      }

      const syncOptions = connection.last_sync_at
        ? { dateFrom: connection.last_sync_at, dateTo: new Date().toISOString() }
        : { lookbackDays: 7, dateTo: new Date().toISOString() };
      const nativePayload = await HealthConnect.syncHealthConnectPhase1(syncOptions);
      if (!nativePayload?.available) {
        toast.error(t('profile.wearables.healthConnectUnavailable'));
        return;
      }
      if (nativePayload?.permissionsMissing) {
        await reportHealthConnectSourceStatus({
          status: 'permission_required',
          background_sync_enabled: Boolean(backgroundSyncStatus?.scheduled),
          error_code: 'permission_required',
        }, connection);
        toast.error(t('profile.wearables.healthPermissionsRequired'));
        return;
      }

      const importPayload = buildHealthConnectImportPayload(nativePayload, {
        device_id: connection.id,
        app_instance_id: connection.id,
        background_sync_enabled: Boolean(backgroundSyncStatus?.scheduled),
      });
      const importItemCount = importPayload.metrics.length + importPayload.workouts.length;
      if (importItemCount === 0) {
        const syncCompletedAt = new Date().toISOString();
        const backgroundStatus = await scheduleHealthConnectBackgroundSync(
          connection,
          nativePayload?.syncWindow?.dateTo || syncCompletedAt
        );
        await reportHealthConnectSourceStatus({
          status: 'no_new_data',
          background_sync_enabled: Boolean(backgroundStatus?.scheduled),
          last_sync_finished_at: syncCompletedAt,
          last_provider_timestamp: nativePayload?.syncWindow?.dateTo || syncCompletedAt,
          error_code: backgroundStatus?.errorCode || null,
        }, connection);
        notifyBackgroundSyncIssue(backgroundStatus);
        toast(t('profile.wearables.noNewWearableData'));
        return;
      }

      const importResponse = await connectedTraining.importData(importPayload);
      const importResult = importResponse?.data || {};

      const syncCompletedAt = new Date().toISOString();
      const lastProviderTimestamp =
        healthConnectLastProviderTimestamp(nativePayload) ||
        nativePayload?.syncWindow?.dateTo ||
        syncCompletedAt;
      await wearables.updateConnection(connection.id, {
        status: 'connected',
        granted_scopes: Array.isArray(nativePayload?.grantedScopes) ? nativePayload.grantedScopes : connection.granted_scopes,
        last_sync_at: syncCompletedAt,
        last_provider_timestamp: lastProviderTimestamp,
        error_code: null,
      });
      const backgroundStatus = await scheduleHealthConnectBackgroundSync(connection, lastProviderTimestamp);
      await reportHealthConnectSourceStatus({
        status:
          Number(importResult.imported_metrics_count || 0) || Number(importResult.imported_workouts_count || 0)
            ? 'success'
            : 'no_new_data',
        background_sync_enabled: Boolean(backgroundStatus?.scheduled),
        last_sync_finished_at: syncCompletedAt,
        last_provider_timestamp: lastProviderTimestamp,
        last_import_run_id: importResult.import_run_id || null,
        imported_metrics_count: Number(importResult.imported_metrics_count || 0),
        imported_workouts_count: Number(importResult.imported_workouts_count || 0),
        skipped_count:
          Number(importResult.skipped_metrics_count || 0) +
          Number(importResult.skipped_workouts_count || 0) +
          Number(importResult.skipped_sessions_count || 0),
        rejected_count: Number(importResult.rejected_count || 0),
      }, connection);
      notifyBackgroundSyncIssue(backgroundStatus);
      await fetchConnections();

      // Health Connect sync now writes through the canonical connected-training
      // import pipeline. Legacy /wearables writes stay disabled here to avoid
      // double-counting the same phone records in two backend stores.
      toast.success(
        `${t('profile.wearables.wearableDataSynced')}. ` +
          `${t('profile.wearables.samplesSynced')}: ${Number(importResult.imported_metrics_count || 0)}. ` +
          `${t('profile.wearables.workoutsSynced')}: ${Number(importResult.imported_workouts_count || 0)}. ` +
          `${t('profile.wearables.skippedRecords')}: ${
            Number(importResult.skipped_metrics_count || 0) + Number(importResult.skipped_workouts_count || 0)
          }. ` +
          `${t('connectedTraining.rejectedRecords')}: ${Number(importResult.rejected_count || 0)}. ` +
          `${t('connectedTraining.energyExpendituresCreated')}: ${Number(importResult.energy_expenditures_created || 0)}`
      );
      if (
        !Number(importResult.imported_metrics_count || 0) &&
        !Number(importResult.imported_workouts_count || 0)
      ) {
        toast(t('profile.wearables.noNewWearableData'));
      }
    } catch (error) {
      await reportHealthConnectSourceStatus({
        status: 'failed',
        background_sync_enabled: Boolean(backgroundSyncStatus?.scheduled),
        last_sync_finished_at: new Date().toISOString(),
        error_code: 'foreground_sync_failed',
      }, connection);
      toast.error(t('profile.wearables.failedToSyncWearableData'));
    } finally {
      setSyncingId(null);
    }
  };

  const ensureAppleHealthPermissions = async () => {
    const availability = await AppleHealth.isAppleHealthAvailable();
    if (!availability?.available) {
      toast.error(t('profile.wearables.appleHealthUnavailable'));
      return false;
    }

    const currentStatus = AppleHealth.getAppleHealthAuthorizationStatus
      ? await AppleHealth.getAppleHealthAuthorizationStatus()
      : null;
    if (hasAppleHealthRequiredPermissions(currentStatus)) {
      return true;
    }

    const requested = await AppleHealth.requestAppleHealthPermissions();
    if (!hasAppleHealthRequiredPermissions(requested)) {
      toast.error(t('profile.wearables.appleHealthPermissionsRequired'));
      return false;
    }
    return true;
  };

  const handleSyncAppleHealth = async (connection) => {
    if (!connection?.id) return;
    setSyncingId(connection.id);
    try {
      const hasPermissions = await ensureAppleHealthPermissions();
      if (!hasPermissions) {
        await reportAppleHealthSourceStatus({
          status: 'permission_required',
          error_code: 'permission_required',
        }, connection);
        return;
      }

      const syncOptions = connection.last_sync_at
        ? { dateFrom: connection.last_sync_at, dateTo: new Date().toISOString() }
        : { lookbackDays: 7, dateTo: new Date().toISOString() };
      const nativePayload = await AppleHealth.readAppleHealthPhase1(syncOptions);
      if (!nativePayload?.available) {
        await reportAppleHealthSourceStatus({
          status: 'failed',
          error_code: 'apple_health_unavailable',
        }, connection);
        toast.error(t('profile.wearables.appleHealthUnavailable'));
        return;
      }
      if (nativePayload?.permissionsMissing) {
        await reportAppleHealthSourceStatus({
          status: 'permission_required',
          error_code: 'permission_required',
        }, connection);
        toast.error(t('profile.wearables.appleHealthPermissionsRequired'));
        return;
      }

      const importPayload = buildAppleHealthImportPayload(nativePayload, {
        device_id: connection.id,
        app_instance_id: connection.id,
        background_sync_enabled: false,
      });
      const importItemCount = importPayload.metrics.length + importPayload.workouts.length;
      const syncCompletedAt = new Date().toISOString();
      const lastProviderTimestamp =
        appleHealthLastProviderTimestamp(nativePayload) ||
        nativePayload?.syncWindow?.dateTo ||
        syncCompletedAt;

      if (importItemCount === 0) {
        await wearables.updateConnection(connection.id, {
          status: 'connected',
          granted_scopes: Array.isArray(nativePayload?.grantedScopes)
            ? nativePayload.grantedScopes
            : connection.granted_scopes,
          last_sync_at: syncCompletedAt,
          last_provider_timestamp: lastProviderTimestamp,
          error_code: null,
        });
        await reportAppleHealthSourceStatus({
          status: 'no_new_data',
          last_sync_finished_at: syncCompletedAt,
          last_provider_timestamp: lastProviderTimestamp,
          error_code: null,
        }, connection);
        await fetchConnections();
        toast(t('profile.wearables.noNewAppleHealthData'));
        return;
      }

      const importResponse = await connectedTraining.importData(importPayload);
      const importResult = importResponse?.data || {};

      await wearables.updateConnection(connection.id, {
        status: 'connected',
        granted_scopes: Array.isArray(nativePayload?.grantedScopes)
          ? nativePayload.grantedScopes
          : connection.granted_scopes,
        last_sync_at: syncCompletedAt,
        last_provider_timestamp: lastProviderTimestamp,
        error_code: null,
      });
      await reportAppleHealthSourceStatus({
        status:
          Number(importResult.imported_metrics_count || 0) || Number(importResult.imported_workouts_count || 0)
            ? 'success'
            : 'no_new_data',
        last_sync_finished_at: syncCompletedAt,
        last_provider_timestamp: lastProviderTimestamp,
        last_import_run_id: importResult.import_run_id || null,
        imported_metrics_count: Number(importResult.imported_metrics_count || 0),
        imported_workouts_count: Number(importResult.imported_workouts_count || 0),
        skipped_count:
          Number(importResult.skipped_metrics_count || 0) +
          Number(importResult.skipped_workouts_count || 0) +
          Number(importResult.skipped_sessions_count || 0),
        rejected_count: Number(importResult.rejected_count || 0),
      }, connection);
      await fetchConnections();

      toast.success(
        `${t('profile.wearables.appleHealthDataSynced')}. ` +
          `${t('profile.wearables.samplesSynced')}: ${Number(importResult.imported_metrics_count || 0)}. ` +
          `${t('profile.wearables.workoutsSynced')}: ${Number(importResult.imported_workouts_count || 0)}. ` +
          `${t('profile.wearables.skippedRecords')}: ${
            Number(importResult.skipped_metrics_count || 0) + Number(importResult.skipped_workouts_count || 0)
          }. ` +
          `${t('connectedTraining.rejectedRecords')}: ${Number(importResult.rejected_count || 0)}`
      );
      if (
        !Number(importResult.imported_metrics_count || 0) &&
        !Number(importResult.imported_workouts_count || 0)
      ) {
        toast(t('profile.wearables.noNewAppleHealthData'));
      }
    } catch (error) {
      await reportAppleHealthSourceStatus({
        status: 'failed',
        last_sync_finished_at: new Date().toISOString(),
        error_code: 'apple_health_foreground_sync_failed',
      }, connection);
      toast.error(t('profile.wearables.failedToSyncAppleHealthData'));
    } finally {
      setSyncingId(null);
    }
  };

  const handleConnectAppleHealth = async () => {
    setConnectingAppleHealth(true);
    try {
      const availability = await AppleHealth.isAppleHealthAvailable();
      if (!availability?.available) {
        await reportAppleHealthSourceStatus({
          status: 'failed',
          error_code: 'apple_health_unavailable',
        });
        toast.error(t('profile.wearables.appleHealthUnavailable'));
        return;
      }

      const result = await AppleHealth.requestAppleHealthPermissions();
      if (!hasAppleHealthRequiredPermissions(result)) {
        await reportAppleHealthSourceStatus({
          status: 'permission_required',
          error_code: 'permission_required',
        });
        toast.error(t('profile.wearables.appleHealthPermissionsRequired'));
        return;
      }

      const connection = await upsertAppleHealthConnection(appleHealthGrantedPermissions(result));
      await reportAppleHealthSourceStatus({
        status: 'disabled',
        error_code: null,
      }, connection);
      await fetchConnections();
      toast.success(t('profile.wearables.appleHealthConnected'));
      await handleSyncAppleHealth(connection);
    } catch (error) {
      toast.error(t('profile.wearables.permissionRequestCancelled'));
    } finally {
      setConnectingAppleHealth(false);
    }
  };

  const hasCanonicalSyncStatus = mobileSourceStatuses.length > 0;
  const equipmentCapabilitySummary = useMemo(
    () => summarizeEquipmentCapabilities(equipmentCapabilities),
    [equipmentCapabilities]
  );
  const equipmentDeliverySummary = useMemo(
    () => summarizeEquipmentDeliveries(equipmentDeliveries),
    [equipmentDeliveries]
  );
  const latestCapabilityTone = equipmentCapabilityTone(equipmentCapabilitySummary.latestCapability);
  const latestDeliveryTone = equipmentDeliveryStatusTone(equipmentDeliverySummary.latestStatus);
  const fallbackModeEntries = Object.entries(equipmentDeliverySummary.fallbackModes || {});

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={<Watch size={21} strokeWidth={1.8} />}
        kicker={t('profile.wearables.title')}
        title={t('profile.wearables.devicesAndSync')}
        body={t('profile.wearables.devicesAndSyncBody')}
      />

      {loading ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm font-bold text-zinc-500">
          {t('common.loading')}
        </div>
      ) : null}

      {loadFailed ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-4">
          <p className="text-sm font-black text-rose-700">{t('profile.wearables.connectionError')}</p>
          <button
            type="button"
            onClick={fetchConnections}
            className="mt-3 inline-flex min-h-10 items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-sm font-black text-rose-700 transition hover:bg-rose-100"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      <div className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
        <div className="mb-3">
          <p className="text-xs font-black uppercase tracking-wide text-zinc-500">
            {t('profile.wearables.automaticMobileSync')}
          </p>
          <p className="mt-1 text-xs leading-5 text-zinc-500">
            {t('profile.wearables.syncStatusSource')}
          </p>
        </div>
        {hasCanonicalSyncStatus ? (
          <div className="mb-3 space-y-2">
            {mobileSourceStatuses.map((status) => (
              <div key={status.id || `${status.source}-${status.platform}-${status.device_id || status.app_instance_id || 'source'}`} className="rounded-lg border border-zinc-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-black text-zinc-950">
                    {mobileSourceProviderLabel(status, t)}
                  </span>
                  <span className="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
                    {wearablePlatformLabel(status.platform, t)}
                  </span>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-black ring-1 ${mobileSourceStatusToneClass(status)}`}>
                    {mobileSourceStatusLabel(status, t)}
                  </span>
                </div>
                <div className="mt-2 grid gap-2 text-xs text-zinc-600 sm:grid-cols-2">
                  <div>
                    <span className="font-bold text-zinc-500">{t('profile.wearables.backgroundSyncEnabled')}: </span>
                    <span className="font-black text-zinc-800">
                      {status.background_sync_enabled ? t('common.yes') : t('common.no')}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-zinc-500">{t('profile.wearables.lastSuccessfulSync')}: </span>
                    <span className="font-black text-zinc-800">
                      {formatWearableDate(status.last_successful_sync_at, i18n.language)}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-zinc-500">{t('profile.wearables.lastProviderTimestamp')}: </span>
                    <span className="font-black text-zinc-800">
                      {formatWearableDate(status.last_provider_timestamp, i18n.language)}
                    </span>
                  </div>
                  <div>
                    <span className="font-bold text-zinc-500">{t('profile.wearables.mobileSourceImportedCounts')}: </span>
                    <span className="font-black text-zinc-800">
                      {Number(status.total_imported_metrics_count || 0)} / {Number(status.total_imported_workouts_count || 0)}
                    </span>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-bold text-zinc-500">{t('profile.wearables.mobileSourceSkippedRejectedCounts')}: </span>
                    <span className="font-black text-zinc-800">
                      {Number(status.total_skipped_count || 0)} / {Number(status.total_rejected_count || 0)}
                    </span>
                  </div>
                  {status.last_error_code || status.last_error_message ? (
                    <div className="sm:col-span-2">
                      <span className="font-bold text-zinc-500">{t('profile.wearables.mobileSourceLastError')}: </span>
                      <span className="font-black text-amber-800">
                        {status.last_error_message || status.last_error_code}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mb-3 rounded-lg border border-dashed border-zinc-200 bg-white p-3 text-xs font-bold text-zinc-500">
            {t('profile.wearables.noCanonicalSyncStatus')}
          </div>
        )}
        {androidNative ? (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleConnectHealthConnect}
                disabled={connectingHealthConnect}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Watch size={16} strokeWidth={1.8} />
                {connectingHealthConnect ? t('common.loading') : t('profile.wearables.connectHealthConnect')}
              </button>
              <button
                type="button"
                onClick={handleOpenHealthConnect}
                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 text-sm font-black text-zinc-700 transition hover:bg-zinc-50"
              >
                {t('profile.wearables.openHealthConnect')}
              </button>
            </div>
            <p className="text-xs leading-5 text-zinc-500">
              {t('profile.wearables.healthConnectDataScope')}
            </p>
            {backgroundSyncStatus ? (
              <div className={`rounded-lg px-3 py-2 text-xs font-bold ring-1 ${backgroundSyncToneClass(backgroundSyncStatus)}`}>
                {t('profile.wearables.phoneLocalDetail')}: {backgroundSyncMessage(backgroundSyncStatus, t)}
              </div>
            ) : null}
          </div>
        ) : null}

        {appleHealthNativeAction ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleConnectAppleHealth}
              disabled={connectingAppleHealth}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Watch size={16} strokeWidth={1.8} />
              {connectingAppleHealth ? t('common.loading') : t('profile.wearables.connectAppleHealth')}
            </button>
            <p className="text-xs leading-5 text-zinc-500">
              {t('profile.wearables.appleHealthNativeDataScope')}
            </p>
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
              {t('profile.wearables.appleHealthForegroundOnly')}
            </div>
          </div>
        ) : null}

        {!androidNative && !iosNative ? (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
              {t('profile.wearables.openAndroidAppForAutomaticSync')}
            </span>
            <span className="inline-flex rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
              {t('profile.wearables.appleHealthIosAppRequired')}
            </span>
          </div>
        ) : null}
      </div>

      <div className="mb-4 rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-zinc-500">
              {t('profile.wearables.equipmentAutomation.title')}
            </p>
            <p className="mt-1 text-xs leading-5 text-zinc-500">
              {t('profile.wearables.equipmentAutomation.body')}
            </p>
          </div>
          <button
            type="button"
            onClick={refreshEquipmentAutomationStatus}
            disabled={equipmentAutomationLoading}
            className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 text-xs font-black text-zinc-700 transition hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {equipmentAutomationLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
            {t('profile.wearables.equipmentAutomation.refreshStatus')}
          </button>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-zinc-500">
              {t('profile.wearables.equipmentAutomation.verifiedCapabilities')}
            </p>
            <p className="mt-1 text-2xl font-black text-zinc-950">{equipmentCapabilitySummary.verifiedCount}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-zinc-500">
              {t('profile.wearables.equipmentAutomation.unverifiedCapabilities')}
            </p>
            <p className="mt-1 text-2xl font-black text-zinc-950">{equipmentCapabilitySummary.unverifiedOrDetectedCount}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-zinc-500">
              {t('profile.wearables.equipmentAutomation.workoutDeliverySupport')}
            </p>
            <p className="mt-1 text-2xl font-black text-zinc-950">{equipmentCapabilitySummary.workoutDeliverySupportedCount}</p>
          </div>
        </div>

        <div className="mt-3 grid gap-2 text-xs text-zinc-600 sm:grid-cols-2">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <span className="font-bold text-zinc-500">{t('profile.wearables.equipmentAutomation.readDataSupport')}: </span>
            <span className="font-black text-zinc-800">{equipmentCapabilitySummary.readDataSupportedCount}</span>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <span className="font-bold text-zinc-500">{t('profile.wearables.equipmentAutomation.liveControlStatus')}: </span>
            <span className="font-black text-zinc-800">
              {t('profile.wearables.equipmentAutomation.liveControlUnavailable')}
            </span>
          </div>
        </div>

        <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-bold leading-5 ring-1 ${equipmentAutomationToneClass(latestCapabilityTone)}`}>
          {t(equipmentCapabilitySummary.primaryMessageKey)}
        </div>

        <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-black uppercase tracking-wide text-zinc-500">
              {t('profile.wearables.equipmentAutomation.latestDeliveryStatus')}
            </span>
            <span className={`rounded-full px-2.5 py-1 text-xs font-black ring-1 ${equipmentAutomationToneClass(latestDeliveryTone)}`}>
              {t(equipmentDeliverySummary.latestStatusMessageKey)}
            </span>
          </div>
          <p className="mt-2 text-xs leading-5 text-zinc-500">
            {t('profile.wearables.equipmentAutomation.manualFallbackMessage')}
          </p>
          {fallbackModeEntries.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {fallbackModeEntries.map(([mode, count]) => (
                <span key={mode} className="rounded-full bg-white px-2.5 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
                  {equipmentFallbackLabel(mode, t)} - {count}
                </span>
              ))}
            </div>
          ) : (
            <span className="mt-2 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
              {equipmentFallbackLabel('manual', t)}
            </span>
          )}
        </div>

        {equipmentCapabilitySummary.latestCapability ? (
          <div className={`mt-3 rounded-lg px-3 py-2 text-xs font-bold leading-5 ring-1 ${equipmentAutomationToneClass(latestCapabilityTone)}`}>
            {t(formatEquipmentCapabilityStatus(equipmentCapabilitySummary.latestCapability))}
          </div>
        ) : null}
      </div>

      {!loading && !loadFailed && connections.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4">
          <p className="font-black text-zinc-950">{t('profile.wearables.emptyTitle')}</p>
          <p className="mt-2 text-sm leading-6 text-zinc-500">{t('profile.wearables.emptyBody')}</p>
        </div>
      ) : null}

      {!loading && connections.length ? (
        <div className="space-y-3">
          <p className="text-xs font-black uppercase tracking-wide text-zinc-500">
            {t('profile.wearables.legacyConnectionDetails')}
          </p>
          {connections.map((connection) => {
            const canSyncHealthConnect =
              androidNative &&
              connection?.provider === 'health_connect' &&
              connection?.platform === 'android' &&
              connection?.status === 'connected';
            const canSyncAppleHealth =
              appleHealthNativeAction &&
              connection?.provider === 'healthkit' &&
              connection?.platform === 'ios' &&
              connection?.status === 'connected';

            return (
              <WearableConnectionRow
                key={connection.id}
                connection={connection}
                onDisconnect={handleDisconnect}
                onSync={canSyncAppleHealth ? handleSyncAppleHealth : handleSyncHealthConnect}
                disconnecting={disconnectingId === connection.id}
                syncing={syncingId === connection.id}
                canSyncNative={canSyncHealthConnect || canSyncAppleHealth}
                syncingLabel={
                  canSyncAppleHealth
                    ? t('profile.wearables.syncingAppleHealthData')
                    : t('profile.wearables.syncingWearableData')
                }
                t={t}
                i18n={i18n}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

const DangerZoneCard = ({ onLogout, t }) => (
  <section className="rounded-lg border border-rose-200 bg-rose-50 p-5 shadow-sm">
    <SectionHeader
      icon={<LogOut size={21} strokeWidth={1.8} />}
      kicker={t('profile.session', 'Session')}
      title={t('profile.dangerZone', 'Danger zone')}
      body={t('profile.logoutHint', 'Sign out on this device. This does not delete your account.')}
    />
    <button
      type="button"
      onClick={onLogout}
      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-4 text-sm font-black text-rose-700 transition hover:bg-rose-100"
      data-testid="logout-button"
    >
      <LogOut size={16} strokeWidth={1.8} />
      {t('nav.logout')}
    </button>
  </section>
);

const AccountStatusCard = ({ user, profile, t }) => {
  const rows = [
    [t('auth.email'), user?.email || '-'],
    [t('auth.role'), roleLabel(user?.role, t)],
    [t('profile.city'), profile?.city || '-'],
    [t('profile.level'), profile?.level || '-'],
  ];
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <SectionHeader
        icon={<ShieldCheck size={21} strokeWidth={1.8} />}
        kicker={t('profile.accountStatus', 'Account status')}
        title={t('profile.profileSummary', 'Profile summary')}
      />
      <div className="space-y-3">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-4 rounded-lg bg-zinc-50 px-3 py-2">
            <span className="text-sm font-bold text-zinc-500">{label}</span>
            <span className="truncate text-sm font-black text-zinc-950">{value}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

export const ProfilePage = () => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await athletes.getProfile();
      setProfile(response.data);
      setFormData(formFromProfile(response.data));
    } catch (error) {
      toast.error(t('profile.loadError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await athletes.updateProfile({
        age: formData.age ? parseInt(formData.age, 10) : null,
        body_weight_kg: formData.body_weight_kg ? parseFloat(formData.body_weight_kg) : null,
        date_of_birth: formData.date_of_birth || null,
        country: formData.country || null,
        city: formData.city || null,
        rank: formData.rank || null,
        dominant_hand: formData.dominant_hand || null,
        play_style: formData.play_style || null,
        level: formData.level || null,
      });
      toast.success(t('profile.updateSuccess'));
      fetchProfile();
    } catch (error) {
      toast.error(t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const showAthleteForm = useMemo(() => user?.role === 'athlete', [user?.role]);

  if (loading) return <ProfileSkeleton />;
  if (loadFailed) return <ProfileErrorState onRetry={fetchProfile} t={t} />;

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="profile-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <ProfileHeaderCard user={user} profile={profile} t={t} />

        <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-5">
            {showAthleteForm ? (
              <>
                <ProfileForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  t={t}
                  saving={saving}
                />
                <AthleteDevelopmentProfileCard t={t} i18n={i18n} />
              </>
            ) : (
              <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <SectionHeader
                  icon={<User size={21} strokeWidth={1.8} />}
                  kicker={t('profile.details', 'Details')}
                  title={t('profile.subtitle')}
                  body={t('profile.nonAthleteHint', 'Athlete profile fields are available for athlete accounts.')}
                />
              </section>
            )}
            <NotificationPreferenceCard t={t} />
          </div>

          <aside className="space-y-5">
            <AccountStatusCard user={user} profile={profile} t={t} />
            {showAthleteForm ? <WearablesCard t={t} i18n={i18n} user={user} /> : null}
            <LanguageSelector i18n={i18n} t={t} />
            <SecurityCard user={user} t={t} />
            <PasswordActionCard t={t} />
            <DangerZoneCard onLogout={handleLogout} t={t} />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
